import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { logAdminOperation } from '../../lib/adminActivityLogger';

const StatusManagement = () => {
    const [status, setStatus] = useState('normal');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const statusDocRef = useCallback(() => doc(db, 'system_status', 'current'), []);

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                setLoading(true);
                const docSnap = await getDoc(statusDocRef());
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setStatus(data.status || 'normal');
                    setMessage(data.message || '');
                }
            } catch (err) {
                console.error("Error fetching status:", err);
                setError('ステータスの読み込みに失敗しました。');
            } finally {
                setLoading(false);
            }
        };
        fetchStatus();
    }, [statusDocRef]);

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError(null);
            setSuccess(false);

            await setDoc(statusDocRef(), {
                status,
                message,
                updatedAt: serverTimestamp(),
            });

            // Record Admin Operation Log: status_update
            await logAdminOperation({
                operation_type: 'status_update',
                target_type: 'status',
                target_id: 'current',
                target_name: `障害・メンテナンスステータス (${status})`,
                reason: message || 'ステータス更新'
            });

            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            console.error("Error updating status:", err);
            setError('ステータスの更新に失敗しました。');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div>読み込み中...</div>;
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <h1 className="text-2xl font-bold mb-6">障害・メンテナンス管理</h1>

            {success && <div className="bg-green-100 text-green-700 p-3 rounded-lg mb-4">ステータスを更新しました。</div>}
            {error && <div className="bg-red-100 text-red-700 p-3 rounded-lg mb-4">エラー: {error}</div>}

            <form onSubmit={handleUpdate}>
                <div className="space-y-6">
                    <div>
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700">サイトステータス</label>
                        <select
                            id="status"
                            name="status"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                        >
                            <option value="normal">正常</option>
                            <option value="outage">障害</option>
                            <option value="maintenance">メンテナンス</option>
                        </select>
                        <p className="mt-2 text-sm text-gray-500">
                            「障害」または「メンテナンス」を選択すると、サイト上部にバナーが表示されます。
                        </p>
                    </div>

                    <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                            表示メッセージ
                        </label>
                        <div className="mt-1">
                            <textarea
                                id="message"
                                name="message"
                                rows={4}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 mt-1 block w-full sm:text-sm border border-gray-300 rounded-md p-2"
                                placeholder="例: 現在、請求書作成機能に時間がかかる障害が発生しています。ご迷惑をおかけし、申し訳ございません。"
                            />
                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                            ステータスが「正常」以外の場合に表示されるメッセージです。
                        </p>
                    </div>
                </div>

                <div className="mt-8">
                    <button
                        type="submit"
                        disabled={saving}
                        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
                    >
                        {saving ? '更新中...' : 'ステータスを更新'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default StatusManagement;
